import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "../services/auth.service";
import { RoleService } from "../services/role.service";
import { UserRole } from "../models/user-role.model";
import { environment } from "../../environments/environment";
import { switchMap, take, catchError } from "rxjs/operators";
import { from, of, Observable } from "rxjs";

export const authGuard = (): Observable<boolean> => {
	const authService = inject(AuthService);
	const roleService = inject(RoleService);
	const router = inject(Router);

	return roleService.getCurrentUserWithRoles().pipe(
		take(1),
		switchMap((userWithRoles) => {
			if (!userWithRoles) {
				void router.navigate(["/login"]);
				return of(false);
			}

			const roles = userWithRoles.roles;
			let onlyRespondent = false;

			if (Array.isArray(roles)) {
				onlyRespondent =
					roles.length === 1 && roles.includes(UserRole.RESPONDENT as string);
			} else if (roles && typeof roles === "object") {
				const trueRoles = Object.keys(roles).filter((key) => roles[key] === true);
				onlyRespondent =
					trueRoles.length === 1 && trueRoles.includes(UserRole.RESPONDENT as string);
			}

			if (onlyRespondent) {
				window.location.href = environment.MEMBERS_APP_URL;
				return of(false);
			}

			// Refresh the Firebase token before allowing navigation. This used to
			// live on click handlers on the header's nav links, which meant back /
			// forward, typed URLs and every programmatic router.navigate skipped
			// it. AuthService signs the user out and explains why when the refresh
			// fails.
			return from(authService.checkSessionBeforeNavigation());
		}),
		catchError((error) => {
			// Fail closed: if roles cannot be resolved, deny access and send to login.
			console.error("authGuard: failed to resolve user roles", error);
			void router.navigate(["/login"]);
			return of(false);
		}),
	);
};
